import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { UserPlus, Store, ClipboardList, Tag, ImagePlus, Crown, Check, Image as ImageIcon } from 'lucide-react';
import './GuidaFornitori.css';

// Pagina pubblica "Come funziona" pensata per i nuovi fornitori che si registrano:
// spiega passo passo registrazione, vetrina (profilo pubblico), scheda, listino,
// foto e la differenza Free/Pro. Gli screenshot vivono in /public/guida/*.png:
// finche' non ci sono, lo slot mostra un segnaposto tratteggiato (vedi <Shot/>).

// Slot screenshot con fallback: se l'immagine non esiste mostra un segnaposto
// elegante con la didascalia, cosi' la pagina e' utile anche prima delle catture.
function Shot({ src, alt, caption }) {
  const [failed, setFailed] = useState(false);
  return (
    <figure className="guida-shot">
      {failed ? (
        <div className="guida-shot-placeholder">
          <ImageIcon size={28} />
          <span>Screenshot in arrivo</span>
          {caption && <span style={{ fontStyle: 'italic' }}>{caption}</span>}
        </div>
      ) : (
        <img src={src} alt={alt} loading="lazy" onError={() => setFailed(true)} />
      )}
      {!failed && caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
}

function GuidaFornitori() {
  const { isSignedIn } = useAuth();
  const startTo = isSignedIn ? '/dashboard' : '/registrazione';

  return (
    <div className="guida-page">
      {/* HERO */}
      <header className="guida-hero">
        <span className="guida-kicker">Guida per i fornitori</span>
        <h1>Come creare il tuo profilo su FesteDaPiccoli</h1>
        <p>
          In pochi passi sei online e visibile ai genitori che cercano location,
          animazione, allestimenti e torte per le feste di compleanno. Ecco come fare.
        </p>
        <Link to={startTo} className="home-btn">
          {isSignedIn ? 'Vai alla dashboard' : 'Inizia gratis'}
        </Link>
      </header>

      {/* INDICE */}
      <nav className="guida-toc" aria-label="Indice della guida">
        <a href="#registrazione">1. Registrazione</a>
        <a href="#vetrina">2. La vetrina</a>
        <a href="#scheda">3. La scheda</a>
        <a href="#listino">4. Il listino</a>
        <a href="#foto">5. Le foto</a>
        <a href="#piani">Free o Pro?</a>
      </nav>

      {/* STEP 1 — REGISTRAZIONE */}
      <section className="guida-step" id="registrazione">
        <div className="guida-step-num"><UserPlus size={22} /></div>
        <div className="guida-step-body">
          <h2>1. Registrati e completa il profilo</h2>
          <p>
            Vai su <strong>Area fornitori</strong> e crea il tuo account con email e
            password. Subito dopo ti chiediamo qualche informazione di base per capire
            chi sei:
          </p>
          <ul>
            <li><strong>Tipo di attività</strong> — Location, Intrattenimento, Allestimenti, Food o più categorie.</li>
            <li><strong>Nome dell'attività</strong> — es. "Sala Feste Aurora".</li>
            <li><strong>Città</strong> (e regione) in cui operi.</li>
          </ul>
          <p>
            È tutto gratuito: appena confermi arrivi nella tua <strong>Dashboard</strong>,
            il pannello da cui gestisci tutto.
          </p>
        </div>
      </section>

      {/* STEP 2 — VETRINA */}
      <section className="guida-step" id="vetrina">
        <div className="guida-step-num"><Store size={22} /></div>
        <div className="guida-step-body">
          <h2>2. Allestisci la tua vetrina</h2>
          <div className="guida-callout">
            <strong>Cos'è la vetrina?</strong> È il tuo profilo pubblico: la pagina che i
            genitori vedono con il nome dell'attività, la foto, la zona in cui lavori e i
            tuoi contatti. È il tuo "biglietto da visita" sulla piattaforma.
          </div>
          <p>
            La trovi in <strong>Dashboard → Il mio profilo</strong>. Compila con cura
            soprattutto:
          </p>
          <ul>
            <li><strong>Immagine profilo</strong> — il logo o una foto rappresentativa.</li>
            <li><strong>Località e zone coperte</strong> — dove offri i tuoi servizi.</li>
            <li><strong>Contatti</strong> — telefono, WhatsApp, email, Instagram.</li>
            <li><strong>Descrizione</strong> — racconta chi sei e cosa ti rende speciale.</li>
          </ul>
          <Shot src="/guida/02-vetrina.png" alt="Modifica del profilo / vetrina"
            caption="Il mio profilo: immagine, località, contatti e descrizione della tua attività" />
        </div>
      </section>

      {/* STEP 3 — SCHEDA */}
      <section className="guida-step" id="scheda">
        <div className="guida-step-num"><ClipboardList size={22} /></div>
        <div className="guida-step-body">
          <h2>3. Crea la tua prima scheda</h2>
          <div className="guida-callout">
            <strong>Cos'è una scheda?</strong> È l'annuncio del singolo servizio che
            offri: quello che appare nella ricerca quando un genitore cerca, ad esempio,
            "animazione a Milano". La vetrina sei tu; la scheda è ciò che vendi.
          </div>
          <p>
            Da <strong>Dashboard → Aggiungi scheda</strong> inserisci nome, categoria,
            descrizione, fascia d'età, zona e — soprattutto — il <strong>listino</strong>
            (vedi sotto). Una volta inviata, la scheda viene controllata dal nostro team e
            poi <strong>pubblicata</strong>: la vedrai passare da "In attesa di
            approvazione" a "Pubblicata".
          </p>
          <Shot src="/guida/03-scheda.png" alt="Creazione di una scheda servizio"
            caption="Crea la scheda: informazioni di base, descrizione e dettagli del servizio" />
        </div>
      </section>

      {/* STEP 4 — LISTINO */}
      <section className="guida-step" id="listino">
        <div className="guida-step-num"><Tag size={22} /></div>
        <div className="guida-step-body">
          <h2>4. Compila il listino</h2>
          <div className="guida-callout">
            <strong>Cos'è il listino?</strong> È l'elenco dei tuoi pacchetti, prodotti o
            servizi con i relativi prezzi. Ogni riga è una <strong>voce</strong> (es.
            "Festa Baby - 1 ora", "Torta a tema - al kg"). La fascia di prezzo che appare
            sulla scheda viene calcolata <strong>in automatico</strong> dalle voci che inserisci.
          </div>
          <p>Per ogni voce puoi indicare:</p>
          <ul>
            <li><strong>Titolo e prezzo</strong>, con la modalità giusta: <em>fisso</em>, <em>a partire da</em>, <em>al kg</em>, <em>a pezzo</em> o <em>supplemento</em>.</li>
            <li><strong>Durata, numero di bambini, fascia d'età</strong>.</li>
            <li><strong>Cosa include</strong> (es. location, merenda, gadget finale) e <strong>opzioni</strong> (varianti, gusti, fasce).</li>
          </ul>
          <p>
            Con il piano <strong>Free</strong> puoi inserire <strong>1 voce</strong> nel
            listino; con <strong>Pro</strong> ne hai illimitate.
          </p>
          <Shot src="/guida/04-listino.png" alt="Compilazione del listino con le voci"
            caption="Listino: aggiungi le voci con prezzo, durata e cosa include" />
        </div>
      </section>

      {/* STEP 5 — FOTO */}
      <section className="guida-step" id="foto">
        <div className="guida-step-num"><ImagePlus size={22} /></div>
        <div className="guida-step-body">
          <h2>5. Aggiungi le foto</h2>
          <p>
            Le schede con foto ricevono molti più contatti. Carica immagini luminose e
            reali dei tuoi allestimenti, della location o dei tuoi prodotti. Con il piano
            Free puoi caricare <strong>1 foto</strong> per scheda; con Pro fino a 5.
          </p>
          <Shot src="/guida/05-foto.png" alt="Caricamento foto della scheda"
            caption="Carica le foto direttamente dalla scheda" />
        </div>
      </section>

      {/* PIANI */}
      <section className="guida-step" id="piani">
        <div className="guida-step-num"><Crown size={22} /></div>
        <div className="guida-step-body">
          <h2>Free o Pro?</h2>
          <p>
            Inizi sempre gratis. Passi a <strong>Pro</strong> quando vuoi crescere:
            sblocchi schede e foto illimitate, il listino completo e la possibilità di
            leggere e rispondere ai messaggi dei genitori.
          </p>
          <div className="guida-table-wrap">
            <table className="guida-table">
              <thead>
                <tr>
                  <th>Cosa puoi fare</th>
                  <th>Free</th>
                  <th className="col-pro">Pro</th>
                </tr>
              </thead>
              <tbody>
                <tr><td>Schede pubblicate</td><td>1</td><td className="col-pro">Illimitate</td></tr>
                <tr><td>Foto per scheda</td><td>1</td><td className="col-pro">Fino a 5</td></tr>
                <tr><td>Voci nel listino</td><td>1</td><td className="col-pro">Illimitate</td></tr>
                <tr><td>Profilo pubblico (vetrina)</td><td><Check size={15} /></td><td className="col-pro"><Check size={15} /></td></tr>
                <tr><td>Leggere e rispondere ai messaggi</td><td>—</td><td className="col-pro"><Check size={15} /></td></tr>
              </tbody>
            </table>
          </div>
          <p style={{ fontSize: '0.95rem', color: 'var(--on-surface-variant, #5a403b)' }}>
            Pro costa <strong>€15/mese</strong> oppure <strong>€150/anno</strong> (2 mesi
            gratis). Puoi disdire quando vuoi. <Link to="/piani">Vedi i piani in dettaglio →</Link>
          </p>
        </div>
      </section>

      {/* CTA FINALE */}
      <section className="guida-cta">
        <h2>Pronto a iniziare?</h2>
        <p>Crea il tuo profilo gratis: i genitori della tua zona ti stanno cercando.</p>
        <div className="guida-cta-actions">
          <Link to={startTo} className="home-btn">
            {isSignedIn ? 'Vai alla dashboard' : 'Registrati gratis'}
          </Link>
          <Link to="/piani" className="home-btn home-btn-light">Scopri i piani</Link>
        </div>
      </section>
    </div>
  );
}

export default GuidaFornitori;
